import { useQuery } from '@tanstack/react-query';

import { AdminDashboardOrgDetail, AdminDashboardSummary } from './types';
import { RawDriveListApiResponse } from '../../types';
import { BASE_ADMIN_DASHBOARD_API } from '../../utils/constants';
import { fetchWithAuthV2, fetchWithAuth } from '../../utils/requests';

const fetchAdminDashboardSummary = async (): Promise<AdminDashboardSummary> => {
  const path = `${BASE_ADMIN_DASHBOARD_API}/get_studio_summary_days/0`;
  const response = (await fetchWithAuthV2(path, {
    method: 'GET',
    fallbackErrorMessage: 'failed to fetch Org User List',
  })) as AdminDashboardSummary;

  response.organizations = response.organizations
    .filter((org) => org.status !== null)
    .sort((a, b) => a.organization_id - b.organization_id);

  return response;
};

const fetchAdminDashboardSummaryByRange = async (
  range: number
): Promise<AdminDashboardSummary> => {
  const path = `${BASE_ADMIN_DASHBOARD_API}/get_studio_summary_days/${range}`;
  const response = (await fetchWithAuthV2(path, {
    method: 'GET',
    fallbackErrorMessage: 'failed to fetch Org User List',
  })) as AdminDashboardSummary;
  return response;
};

const fetchAdminDashboardOrgDetail = async (
  organization_id: number,
  page: number,
  per_page: number
): Promise<AdminDashboardOrgDetail> => {
  const path = `${BASE_ADMIN_DASHBOARD_API}/get_users_by_org`;
  const response = (await fetchWithAuthV2(path, {
    method: 'POST',
    fallbackErrorMessage: 'failed to fetch Org User List',
    body: JSON.stringify({ 'user-org-id': organization_id, page, per_page }),
  })) as AdminDashboardOrgDetail;

  const users = response.users.filter((user) => user.email !== null);
  response.users = users;

  return response;
};

const fetchStaleRecordings = async (
  page = 1,
  per_page = 20,
  osf_status: 'NOT_STARTED' | 'FAILED' = 'NOT_STARTED'
): Promise<RawDriveListApiResponse> => {
  // Alternative path for stale drives endpoint (uncomment if needed for legacy support):
  const path = `${BASE_ADMIN_DASHBOARD_API}/stale_drives`;
  const queryParams = new URLSearchParams({
    page: page.toString(),
    per_page: per_page.toString(),
    osf_status,
  });

  const response = await fetchWithAuth(`${path}?${queryParams}`);
  const json = (await response.json()) as RawDriveListApiResponse;

  return json;
};

export const useAdminDashboardSummary = () =>
  useQuery<AdminDashboardSummary, Error>({
    queryKey: ['adminDashboardSummary'],
    queryFn: fetchAdminDashboardSummary,
    staleTime: Infinity,
    gcTime: 900000, // Data is cached for 15 minutes
    refetchOnWindowFocus: false, //
    refetchOnMount: false,
  });

export const useAdminDashboardSummaryByRange = (range: number) =>
  useQuery<AdminDashboardSummary, Error>({
    queryKey: ['adminDashboardSummaryByRange', range],
    queryFn: () => fetchAdminDashboardSummaryByRange(range),
    staleTime: Infinity,
    gcTime: 900000, // Data is cached for 15 minutes
    refetchOnWindowFocus: false, //
    refetchOnMount: false,
  });

export const useAdminDashboardOrgDetail = (
  organization_id: number,
  page: number,
  per_page: number
) =>
  useQuery<AdminDashboardOrgDetail, Error>({
    queryKey: ['adminDashboardOrgDetail', organization_id, page, per_page],
    queryFn: () =>
      fetchAdminDashboardOrgDetail(organization_id, page, per_page),
    staleTime: 5 * 60 * 1000, // Data is cached for 5 minutes
    gcTime: 900000, // Data is cached for 15 minutes
    refetchOnWindowFocus: false, //
    refetchOnMount: false,
  });

export const useStaleRecordings = (
  page = 1,
  per_page = 20,
  osf_status: 'NOT_STARTED' | 'FAILED' = 'NOT_STARTED',
  enabled = true
) => {
  const {
    data: { data = [], total_count: totalCount } = {},
    isError,
    isRefetching,
    isLoading,
    isSuccess,
    refetch,
  } = useQuery<RawDriveListApiResponse, Error>({
    queryKey: ['staleRecordings', page, per_page, osf_status],
    queryFn: () => fetchStaleRecordings(page, per_page, osf_status),
    staleTime: 5 * 60 * 1000, // Data is cached for 5 minutes
    gcTime: 900000, // Data is cached for 15 minutes
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchInterval: 5 * 60 * 1000,
    enabled,
  });

  return {
    data,
    totalCount,
    isError,
    isRefetching,
    isLoading,
    isSuccess,
    refetch,
  };
};
